/**
 * Campaign Candidates Page
 *
 * Lists all candidates associated with an autonomous campaign,
 * with status, evaluation score, and contact info.
 *
 * Route: /campaigns/autonomous/:session_id/candidates
 */

import {
  ArrowLeft,
  Users,
  Target,
  CheckCircle2,
  Clock,
  MessageCircle,
  Mail,
  Phone,
  MessageSquare,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { contactsApi } from '../services/contactsApi';

function CampaignCandidates({ previewMode = false }) {
  const { session_id } = useParams();
  const navigate = useNavigate();
  const [goalId, setGoalId] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [commOpen, setCommOpen] = useState(false);
  const [commLoading, setCommLoading] = useState(false);
  const [commError, setCommError] = useState(null);
  const [commData, setCommData] = useState(null);
  const [commCandidateLabel, setCommCandidateLabel] = useState('');
  const [commTab, setCommTab] = useState('email');

  const isPreview = previewMode || !session_id;

  const statusConfig = {
    sourced: { label: 'Sourced', color: 'bg-blue-100 text-blue-800', icon: Users },
    qualified: { label: 'Qualified', color: 'bg-purple-100 text-purple-800', icon: Target },
    contacted: { label: 'Contacted', color: 'bg-yellow-100 text-yellow-800', icon: MessageCircle },
    interested: { label: 'Interested', color: 'bg-amber-100 text-amber-800', icon: CheckCircle2 },
    scheduled: { label: 'Scheduled', color: 'bg-green-100 text-green-800', icon: CheckCircle2 }
  };

  useEffect(() => {
    if (isPreview) {
      setCandidates([
        {
          contactName: 'Jane Smith',
          email: 'jane@example.com',
          company: 'Acme Inc',
          status: 'qualified',
          evaluation_score: 85,
          resume_snippet: 'Summary: Experienced operator with direct domain fit and strong outcomes.'
        },
        {
          contactName: 'John Doe',
          email: 'john@example.com',
          company: 'Tech Corp',
          status: 'contacted',
          evaluation_score: 78,
          resume_snippet: 'Summary: Skilled in adjacent roles with transferable skills and relevant leadership.'
        }
      ]);
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        let resolvedGoalId = localStorage.getItem(`goal_id_${session_id}`);
        if (!resolvedGoalId) {
          const res = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/session/${session_id}/goal`);
          if (res.ok) {
            const data = await res.json();
            resolvedGoalId = data.goal_id;
            if (resolvedGoalId) {
              localStorage.setItem(`goal_id_${session_id}`, resolvedGoalId);
            }
          }
        }
        if (!resolvedGoalId) {
          setCandidates([]);
          setLoading(false);
          return;
        }
        setGoalId(resolvedGoalId);

        const res = await fetch(
          `${API_BASE_URL}/api/campaigns/autonomous/${resolvedGoalId}/candidates-with-communication`
        );
        if (!res.ok) throw new Error('Failed to fetch candidates');
        const data = await res.json();
        setCandidates(data.candidates || []);
      } catch (error) {
        console.error('Error loading candidates:', error);
        setCandidates([]);
      } finally {
        setLoading(false);
      }
    };

    if (session_id) load();
  }, [session_id, isPreview]);

  const filteredCandidates =
    statusFilter === 'all' ? candidates : candidates.filter((c) => c.status === statusFilter);

  const getStatusBadge = (status) => {
    const cfg = statusConfig[status] || { label: status || 'Unknown', color: 'bg-gray-100 text-gray-800', icon: Clock };
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
        <Icon className="w-3 h-3" />
        {cfg.label}
      </span>
    );
  };

  const formatMaybeDate = (value) => {
    if (!value) return '—';
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString();
    } catch {
      return String(value);
    }
  };

  /**
   * VAPI end reasons that trigger follow-up scheduling in app.py `vapi_webhook`
   * (keep in sync with `followup_reasons` there).
   */
  const VAPI_FOLLOWUP_TRIGGER_REASONS = new Set([
    'customer-did-not-answer',
    'customer-busy',
    'customer-ended-call',
    'voicemail',
    'silence-timed-out',
    'twilio-failed-to-connect-call',
    'vonage-failed-to-connect-call',
    'phone-call-provider-closed-websocket',
    'call.in-progress.error-sip-telephony-provider-failed-to-connect-call',
    'busy',
    'no-answer',
    'customer-did-not-give-microphone-permission',
    'call.in-progress.error-assistant-did-not-receive-customer-audio'
  ]);

  const getCallEndReason = (call) => {
    const raw =
      call?.end_reason ??
      call?.endReason ??
      call?.endedReason ??
      call?.end_reason_code;
    if (raw == null || String(raw).trim() === '') return null;
    return String(raw).trim();
  };

  const isFollowUpTriggerReason = (reason) =>
    reason != null && VAPI_FOLLOWUP_TRIGGER_REASONS.has(reason);

  const isFollowUpCallEntry = (call) =>
    Boolean(call?.is_followup || call?.isFollowup);

  /** Short preview for table cell; full text stays available in comms / backend. */
  const getSummaryPreview = (c) => {
    const raw =
      c.resume_snippet ||
      c.resume_summary ||
      (typeof c.workExperience === 'string' && c.workExperience ? c.workExperience : '') ||
      '';
    if (!raw) return null;
    const compact = String(raw).replace(/\s+/g, ' ').trim();
    const maxLen = 220;
    if (compact.length <= maxLen) return compact;
    return `${compact.slice(0, maxLen)}…`;
  };

  const getResumeId = (c) => c?.id || c?.resume_id || c?.candidate_id || c?.resumeId || null;

  const openCommunication = async (c) => {
    const rid = getResumeId(c);
    const label = c.contactName || c.name || 'Candidate';
    setCommCandidateLabel(label);
    setCommTab('email');
    setCommData(null);
    setCommError(null);
    if (!rid || !goalId || isPreview) {
      setCommError(!rid ? 'No resume id on this row — cannot load communication.' : 'Open a live campaign to view communication.');
      setCommOpen(true);
      return;
    }
    setCommOpen(true);
    setCommLoading(true);
    try {
      const data = await contactsApi.getCampaignCandidateCommunication(goalId, rid);
      if (data.success) {
        setCommData(data);
      } else {
        setCommError(data.error || 'Failed to load communication');
      }
    } catch (e) {
      setCommError(e.message || 'Failed to load communication');
    } finally {
      setCommLoading(false);
    }
  };

  const renderHistoryList = (items, renderItem) => {
    if (!items || items.length === 0) {
      return <p className="text-sm text-gray-500 py-4">No entries yet.</p>;
    }
    return (
      <ul className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
        {items.map((item, idx) => (
          <li
            key={idx}
            className="border border-gray-100 rounded-lg p-3 bg-gray-50 text-sm text-gray-800"
          >
            {renderItem(item, idx)}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/results`)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
              >
                Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Campaign Candidates</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {filteredCandidates.length} candidate
                  {filteredCandidates.length !== 1 ? 's' : ''} in this campaign
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="text-sm text-gray-600">
            Filter:
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
          >
            <option value="all">All</option>
            <option value="sourced">Sourced</option>
            <option value="qualified">Qualified</option>
            <option value="contacted">Contacted</option>
            <option value="interested">Interested</option>
            <option value="scheduled">Scheduled</option>
          </select>
        </div>
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
          </div>
        ) : filteredCandidates.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-lg font-medium text-gray-900 mb-2">No candidates yet</h2>
            <p className="text-gray-500 mb-4">
              Candidates will appear here as the campaign progresses through sourcing and evaluation.
            </p>
            <button
              onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/results`)}
              className="px-4 py-2 text-sm font-medium text-orange-600 hover:text-orange-700"
            >
              Back to Results
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-[1100px] w-full table-fixed divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="sticky left-0 z-20 w-[13%] min-w-[9rem] bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200 shadow-[4px_0_6px_-4px_rgba(0,0,0,0.12)]">
                      Name
                    </th>
                    <th className="w-[15%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="w-[11%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="w-[9%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="w-[6%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                    <th className="w-[14%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last touch</th>
                    <th className="w-[26%] min-w-[200px] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Summary</th>
                    <th className="sticky right-0 z-20 w-[6%] min-w-[5.5rem] border-l border-gray-200 bg-gray-50 px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.12)]">
                      Comms
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredCandidates.map((c, i) => (
                    <tr key={c.email || c.id || i} className="group hover:bg-gray-50">
                      <td className="sticky left-0 z-10 w-[13%] min-w-[9rem] whitespace-nowrap bg-white px-4 py-4 border-r border-gray-100 shadow-[4px_0_6px_-4px_rgba(0,0,0,0.08)] group-hover:bg-gray-50">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-orange-700">
                              {(c.contactName || c.name || '?')[0]}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {c.contactName || c.name || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-4 align-top text-sm text-gray-600 break-all">
                        {c.email || '—'}
                      </td>
                      <td className="px-3 py-4 align-top text-sm text-gray-600 break-words">
                        {c.company ||
                          c.companyName ||
                          c.employer ||
                          c.organization ||
                          (Array.isArray(c.workExperience) && c.workExperience[0] && c.workExperience[0].company
                            ? c.workExperience[0].company
                            : '—')}
                      </td>
                      <td className="px-3 py-4 align-top whitespace-nowrap">
                        {getStatusBadge(c.status)}
                      </td>
                      <td className="px-3 py-4 align-top whitespace-nowrap">
                        {c.evaluation_score != null || c.score != null ? (
                          <span className="text-sm font-medium text-gray-900">
                            {c.evaluation_score ?? c.score}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-4 align-top">
                        {c.communication_summary?.has_communication ? (
                          <div className="text-sm text-gray-600 break-words">
                            <div className="font-medium text-gray-900">
                              {c.communication_summary?.last_method || '—'}
                              {c.communication_summary?.last_direction ? (
                                <span className="text-gray-500"> · {c.communication_summary.last_direction}</span>
                              ) : null}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatMaybeDate(c.communication_summary?.last_contact)}
                              {c.communication_summary?.email?.last_event_type ? (
                                <span> · email:{c.communication_summary.email.last_event_type}</span>
                              ) : null}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-4 align-top min-w-0">
                        <p
                          className="text-sm text-gray-600 line-clamp-4 break-words"
                          title={getSummaryPreview(c) || undefined}
                        >
                          {getSummaryPreview(c) || '—'}
                        </p>
                      </td>
                      <td className="sticky right-0 z-10 w-[6%] min-w-[5.5rem] whitespace-nowrap border-l border-gray-200 bg-gray-50 px-3 py-4 text-right shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-100">
                        <button
                          type="button"
                          onClick={() => openCommunication(c)}
                          className="inline-flex items-center justify-center rounded-md border border-orange-200 bg-white px-3 py-1.5 text-sm font-medium text-orange-700 shadow-sm hover:bg-orange-50 hover:border-orange-300"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-6">
          <button
            onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/results`)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Results
          </button>
        </div>

        {commOpen ? (
          <div className="fixed inset-0 z-50 flex justify-end">
            <button
              type="button"
              className="absolute inset-0 bg-black/40"
              aria-label="Close panel"
              onClick={() => setCommOpen(false)}
            />
            <div className="relative w-full max-w-lg bg-white shadow-xl h-full overflow-y-auto flex flex-col">
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Communication</h2>
                  <p className="text-sm text-gray-500">{commCandidateLabel}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setCommOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="px-4 py-2 border-b border-gray-100 flex gap-1 flex-wrap">
                {[
                  { id: 'email', label: 'Email', Icon: Mail },
                  { id: 'calls', label: 'Calls', Icon: Phone },
                  { id: 'whatsapp', label: 'WhatsApp', Icon: MessageSquare },
                  { id: 'sms', label: 'SMS', Icon: MessageCircle }
                ].map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setCommTab(id)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${
                      commTab === id
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                ))}
              </div>

              <div className="flex-1 px-4 py-4">
                {commLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
                  </div>
                ) : commError ? (
                  <p className="text-sm text-red-600">{commError}</p>
                ) : commData ? (
                  <>
                    {commTab === 'email' &&
                      renderHistoryList(commData.emailHistory, (e) => (
                        <>
                          <div className="font-medium text-gray-900">
                            {e.subject || '(no subject)'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {formatMaybeDate(e.timestamp || e.sentAt)}
                            {e.status ? ` · ${e.status}` : ''}
                          </div>
                          {e.content || e.body ? (
                            <pre className="mt-2 whitespace-pre-wrap text-xs text-gray-700 font-sans max-h-40 overflow-y-auto">
                              {e.content || e.body}
                            </pre>
                          ) : null}
                        </>
                      ))}
                    {commTab === 'calls' &&
                      renderHistoryList(commData.callHistory, (call) => {
                        const endReason = getCallEndReason(call);
                        const followUpEntry = isFollowUpCallEntry(call);
                        const matchesFollowUpRules =
                          Boolean(endReason) &&
                          isFollowUpTriggerReason(endReason) &&
                          !followUpEntry;
                        return (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="text-xs text-gray-500">
                              {formatMaybeDate(call.timestamp)}
                              {call.duration != null ? ` · ${call.duration}s` : ''}
                              {call.status ? ` · ${call.status}` : ''}
                            </div>
                            {followUpEntry ? (
                              <span
                                className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800"
                                title="This row is a follow-up call attempt."
                              >
                                Follow-up call
                              </span>
                            ) : null}
                            {matchesFollowUpRules ? (
                              <span
                                className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900"
                                title="Server rules treat this end reason as eligible to schedule another VAPI call (when job_id, call context, and attempt limits allow). Check logs or auto_followup_attempts if needed."
                              >
                                Auto follow-up
                              </span>
                            ) : null}
                          </div>
                          {endReason ? (
                            <div className="mt-1.5 text-xs">
                              <span className="font-medium text-gray-700">End reason:</span>{' '}
                              <code className="rounded bg-gray-200/80 px-1.5 py-0.5 text-[11px] text-gray-900">
                                {endReason}
                              </code>
                            </div>
                          ) : null}
                          {call.recordingUrl ? (
                            <a
                              href={call.recordingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-orange-600 hover:underline mt-1 inline-block"
                            >
                              Open recording
                            </a>
                          ) : null}
                          {call.transcription ? (
                            <pre className="mt-2 whitespace-pre-wrap text-xs text-gray-800 font-sans max-h-48 overflow-y-auto">
                              {call.transcription}
                            </pre>
                          ) : (
                            <p className="text-xs text-gray-500 mt-2">No transcript stored on this call.</p>
                          )}
                        </>
                        );
                      })}
                    {commTab === 'whatsapp' &&
                      renderHistoryList(commData.whatsappHistory, (w) => (
                        <>
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>{w.direction || '—'}</span>
                            <span>{formatMaybeDate(w.timestamp)}</span>
                          </div>
                          <p className="mt-1 whitespace-pre-wrap">{w.content || w.body || '—'}</p>
                        </>
                      ))}
                    {commTab === 'sms' &&
                      renderHistoryList(commData.smsHistory, (s) => (
                        <>
                          <div className="text-xs text-gray-500">{formatMaybeDate(s.timestamp)}</div>
                          <p className="mt-1 whitespace-pre-wrap">{s.content || '—'}</p>
                        </>
                      ))}
                  </>
                ) : null}
                {!commLoading && !commError && commData && (
                  <p className="mt-4 text-xs text-gray-400">
                    Data comes from the candidate resume record (same source as Contacts). If a channel
                    is empty, logging may not have run for that channel yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default CampaignCandidates;
